import React from 'react';
import { Link } from "react-router-dom";

import logo from '../../images/icon-tertiary.png';
import air from '../../images/doshas/air.svg';
import fire from '../../images/doshas/fire.svg';
import earth from '../../images/doshas/earth.svg';
import ether from '../../images/doshas/ether.svg';
import water from '../../images/doshas/water.svg';
import land from '../../images/land.svg';
import plant2 from '../../images/plant2.svg';
import sunflower from '../../images/sunflower.svg';
import i18n from '../../i18n';

import SubscribeForm from '../../components/SubscribeForm';
import SocialButtons from '../../components/SocialButtons';

const HeroSection = () => {
  return (
    <section className="relative h-screen flex items-center justify-center bg-gradient-to-b from-blue-500 to-green-500 overflow-hidden pt-16 lg:pt-20">
      {/* Background decorations */}
      <div className="absolute inset-0 opacity-10">
        <img
          src={sunflower}
          alt=""
          className="absolute top-20 right-10 w-32 h-32 transform rotate-12 animate-pulse"
        />
        <img
          src={plant2}
          alt=""
          className="absolute bottom-20 left-10 w-24 h-24 transform -rotate-12 animate-pulse"
        />
      </div>

      <div className="relative z-10 text-center px-4 max-w-6xl mx-auto">

        {/* Main heading */}
        <div className="animate-fade-in-up">
          <h1 className="font-display text-white mb-6">
            <div className="text-5xl md:text-7xl lg:text-8xl font-bold mb-2">
              {i18n.header}
            </div>
            <div className="text-3xl md:text-5xl lg:text-6xl font-light opacity-90">
              {i18n.header2}
            </div>
          </h1>

          <div className="text-blue-100 font-body text-xl md:text-2xl lg:text-3xl font-light mb-12 max-w-4xl mx-auto">
            <div className="mb-2">{i18n.subheader}</div>
            <div className="font-medium text-green-200">{i18n.subheader2}</div>
          </div>
        </div>

        {/* CTA buttons */}
        <div className="animate-fade-in-up flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
          <a
            href="https://www.fresha.com/providers/swami-ayurveda-ia5mxbwa?allOffer=true"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary font-display font-semibold text-lg rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-xl shadow-lg backdrop-blur-sm border-2 border-white/20 no-underline min-w-[200px]"
          >
            {i18n.book_appointment}
          </a>
          <a
            href="#about-section"
            className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-display font-medium text-lg rounded-full border-2 border-white/30 backdrop-blur-sm transition-all duration-300 hover:bg-white/20 hover:border-white/50 no-underline min-w-[200px]"
          >
            {i18n.learn_more}
          </a>
        </div>

        {/* Location info */}
        <div className="animate-fade-in">
          <Link
            to="/about"
            className="text-white hover:text-white transition-colors duration-200 text-lg no-underline"
          >
            Open in Ventura, CA, USA
          </Link>
        </div>

        {/* Social buttons */}
        <div className="mt-8 animate-fade-in">
          <SocialButtons light />
        </div>
      </div>
    </section>
  );
};

const AboutSection = () => {
  return (
    <section id="about-section" className="py-16 bg-warm-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <div className="order-2 lg:order-1 animate-fade-in-up hidden lg:block">
            <div className="relative">
              <img src={land} alt="Ayurveda illustration" className="w-full h-auto rounded-2xl" />
            </div>
          </div>

          {/* Content */}
          <div className="order-1 lg:order-2 animate-fade-in-up">
            <div className="mb-8">
              <span className="text-blue-600 font-body font-semibold text-lg tracking-wide uppercase block mb-3">
                Ancient Wisdom
              </span>
              <h2 className="font-display text-primary-dark text-4xl">
                {i18n.what_is_ayurveda}
              </h2>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 leading-relaxed text-lg mb-6">
                {i18n.ayurveda_desc}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col h-full">
                  <div className="text-primary font-semibold mb-2">🌿 Natural Healing</div>
                  <p className="text-gray-600 text-sm flex-grow">Using herbs, oils, and traditional practices</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col h-full">
                  <div className="text-primary font-semibold mb-2">⚖️ Balance & Harmony</div>
                  <p className="text-gray-600 text-sm flex-grow">Aligning mind, body, and spirit</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col h-full">
                  <div className="text-primary font-semibold mb-2">🎯 Personalized Care</div>
                  <p className="text-gray-600 text-sm flex-grow">Treatments based on your unique constitution</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col h-full">
                  <div className="text-primary font-semibold mb-2">🕰️ Time-Tested</div>
                  <p className="text-gray-600 text-sm flex-grow">5,000+ years of proven healing wisdom</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const DoshaSection = () => {
  const doshas = [
    {
      name: 'Vata',
      elements: [
        { name: i18n.ether, icon: ether },
        { name: i18n.air, icon: air }
      ],
      responsibility: 'Movement in the body',
      qualities: 'Light · Dry · Cold · Subtle · Mobile',
      description: 'Governs breathing, circulation, and nervous system function',
      className: 'dosha-vata'
    },
    {
      name: 'Pitta',
      elements: [
        { name: i18n.fire, icon: fire },
        { name: i18n.water, icon: water }
      ],
      responsibility: 'Transformation in the body',
      qualities: 'Hot · Light · Quick · Sharp · Oily',
      description: 'Controls digestion, metabolism, and body temperature',
      className: 'dosha-pitta'
    },
    {
      name: 'Kapha',
      elements: [
        { name: i18n.earth, icon: earth },
        { name: i18n.water, icon: water }
      ],
      responsibility: 'Stability in the body',
      qualities: 'Cold · Heavy · Oily · Soft · Slow · Stable',
      description: 'Provides structure, immunity, and emotional stability',
      className: 'dosha-kapha'
    }
  ];

  return (
    <section className="py-20 bg-cream relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 opacity-5">
        <img
          src={sunflower}
          alt=""
          className="absolute top-10 right-20 w-40 h-40 transform rotate-45"
        />
        <img
          src={plant2}
          alt=""
          className="absolute bottom-10 left-20 w-32 h-32 transform -rotate-45"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4">
        {/* Section header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <span className="text-blue-600 font-body font-semibold text-lg tracking-wide uppercase block mb-4">
            Understanding Your Constitution
          </span>
          <h2 className="font-display text-primary-dark text-4xl mb-8">
            {i18n.fundamentals}
          </h2>
          <div className="max-w-4xl mx-auto space-y-4 text-center">
            <p className="text-gray-600 text-lg leading-relaxed">
              {i18n.dosha_desc}
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              {i18n.dosha_desc2}
            </p>
          </div>
        </div>

        {/* Dosha cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {doshas.map((dosha, index) => (
            <div
              key={dosha.name}
              className={`card-wellness p-8 ${dosha.className} animate-fade-in-up`}
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <div className="text-center">
                <h3 className="font-display text-2xl font-bold mb-6 text-gray-800">
                  {dosha.name}
                </h3>

                {/* Elements */}
                <div className="flex justify-center space-x-6 mb-6">
                  {dosha.elements.map((element) => (
                    <div key={element.name} className="text-center">
                      <div className="w-16 h-16 mx-auto mb-2 bg-white rounded-full flex items-center justify-center shadow-sm">
                        <img
                          src={element.icon}
                          alt={element.name}
                          className="w-10 h-10"
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-700">
                        {element.name}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Description */}
                <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                  {dosha.description}
                </p>

                {/* Responsibility */}
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-800 mb-2">Primary Function</h4>
                  <p className="text-gray-600 text-sm">{dosha.responsibility}</p>
                </div>

                {/* Qualities */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Key Qualities</h4>
                  <p className="text-gray-600 text-sm font-medium">{dosha.qualities}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-16 animate-fade-in-up">
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/dosha-quiz"
              className="btn-primary text-lg px-8 py-4 rounded-full font-display transform hover:scale-105 shadow-lg"
            >
              Take Dosha Assessment
            </Link>
            <Link
              to="/services"
              className="btn-secondary text-lg px-8 py-4 rounded-full font-display"
            >
              View All Treatments
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const NewsletterSection = () => {
  return (
    <section className="py-20 bg-primary text-white relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
        <div className="animate-fade-in-up">
          <h2 className="font-display text-white mb-6">
            Stay Connected to Wellness
          </h2>
          <p className="text-blue-100 text-lg mb-8 leading-relaxed text-center">
            {i18n.sign_up_subscribe}. Get insights on Ayurvedic practices, seasonal wellness tips, and exclusive offers.
          </p>

          <div className="max-w-md mx-auto">
            <SubscribeForm />
          </div>

          <div className="mt-8">
            <SocialButtons light />
          </div>
        </div>
      </div>
    </section>
  );
};

function Home() {
  return (
    <div className="min-h-screen">
      <HeroSection />
      <AboutSection />
      <DoshaSection />
      <NewsletterSection />
    </div>
  );
}

export default Home;

